/***
 *
 *   XPM RECORD TYPE
 *   Maps the `triggered_by` discriminator that the Xero/XPM contact search
 *   stamps on each result row to the label shown in the Direct Verification
 *   results grid. Plain CommonJS with no React so the mapping stays unit
 *   testable from mocha (see test/xpm-record-type.js), matching the
 *   ./australian-identifiers helper alongside it.
 *
 *   The XPM branch of GET /api/integration/xero/contacts flattens practice
 *   clients and their child contacts into a single interleaved list, tagging
 *   every row `xpm_client` or `xpm_contact`
 *   (controller/integrationController.js). Which of the two an operator picks
 *   is not cosmetic: it decides where the AML status is written back, to
 *   `client.api/update/<uuid>` for a client versus `client.api/contact/<uuid>`
 *   for a contact. Adjacent rows routinely share a name and an email address,
 *   so without a visible label there is nothing to tell them apart.
 *
 *   Plain Xero organisation tenants take the other branch and return raw
 *   Accounting API contacts, which carry no `triggered_by` at all. A Xero
 *   contact is a single polymorphic record covering both companies and people
 *   with no reliable discriminator, so those rows get a blank label rather
 *   than a guess that would read as authoritative.
 *
 **********/

const XPM_RECORD_TYPES = {
  xpm_client: { label: "Client", color: "blue" },
  xpm_contact: { label: "Contact", color: "green" },
};

// Rows with no recognised `triggered_by` — every plain Xero organisation
// contact, today. `label` is deliberately an empty string rather than null:
// material-react-table calls .toString() on cell values while filtering and
// faceting, so the accessor built on this must always hand back a string.
// `color` is null to mean "render no badge" — Badge with no colour resolves to
// base-only classes, which is white text on a white background.
const UNKNOWN_RECORD_TYPE = { label: "", color: null };

function getXpmRecordType(triggeredBy) {
  return Object.prototype.hasOwnProperty.call(XPM_RECORD_TYPES, triggeredBy)
    ? XPM_RECORD_TYPES[triggeredBy]
    : UNKNOWN_RECORD_TYPE;
}

function getXpmRecordTypeLabel(triggeredBy) {
  return getXpmRecordType(triggeredBy).label;
}

module.exports = {
  XPM_RECORD_TYPES,
  UNKNOWN_RECORD_TYPE,
  getXpmRecordType,
  getXpmRecordTypeLabel,
};
